import { History } from 'history';
import queryString from 'query-string';

/*
 * Utility class for updating the url (pathname and querystring)
 * There are two common use cases when updating url:
 *   1: Keep existing query params
 *   2: Update queryString and ignore existing query params
 *
 * This helper solves those two use cases among other things
 */

export default class URLBuilder {
  history: History;
  constructor(history: History) {
    this.history = history;
  }

  /*
   * Updates the url based on pathName and queryParams object
   * Either pushes or replaces the history
   * Maintains existing queryParams or ignores them if they are not specified in queryParams
   */
  updateUrl(
    pathName: string,
    queryParams: { [K: string]: string | number | boolean | undefined },
    action: 'Push' | 'Replace',
    maintainExistingQP = false,
  ): void {
    const newUrl = `${pathName}?${this.constructQueryParams(queryParams, maintainExistingQP)}`;
    switch (action) {
      case 'Push':
        this.history.push(newUrl);
        break;
      case 'Replace':
        this.history.replace(newUrl);
        break;
    }
  }

  /*
   * Updates queryParams that are in the object parameter
   * Maintains existing queryParams or ignores them if they are not specified in queryParams
   */
  updateQueryParams(
    queryParams: { [K: string]: string | number | boolean },
    maintainExistingQP = false,
  ): void {
    const newQueryString = this.constructQueryParams(queryParams, maintainExistingQP);
    this.history.replace(`${this.history.location.pathname}?${newQueryString}`);
  }

  /*
   * Removes keys that are in object parameter from queryString
   */
  removeQueryParams(keysToRemove: string[]): void {

    // @ts-ignore
    const currentQueryStringObj: { [K: string]: string } = queryString.parse(
      this.history.location.search,
    );
    const newQueryString = Object.keys(currentQueryStringObj)
      .filter((key) => !keysToRemove.includes(key))
      .map((key) => `${key}=${currentQueryStringObj[key]}`)
      .join('&');
    this.history.replace(`${this.history.location.pathname}?${newQueryString}`);
  }

  /*
   * Constructs query params string
   * Either maintains existing query params or removes them if they are not specified
   */
  private constructQueryParams(
    queryParams: { [K: string]: string | number | boolean | undefined },
    maintainExistingQP: boolean,
  ): string {
    // @ts-ignore
    const currentQueryStringObj: { [K: string]: string } = queryString.parse(
      this.history.location.search,
    );
    const allKeys = {
      ...(maintainExistingQP && currentQueryStringObj),
      ...queryParams,
    };

    return Object.keys(allKeys)
      .map((key) => `${key}=${allKeys[key]}`)
      .join('&');
  }
}
